import { createSelector } from 'reselect';
import { RootReducer } from 'store/reducers';
import {
  Check,
  CheckPayload,
  ReviewResult,
  ReviewResultList,
} from 'types/check';
import { ChecksBrandingAlert } from 'types/check-results';
import {
  COUNTABLE_STATS_CATEGORIES,
  DocumentCategory,
  Review,
  StatisticByStatus,
} from 'types/review-category';
import { nanoid } from 'nanoid';
import { isBrandedDocument } from '../assessment/selectors';
import { selectedCategory, selectedCheck } from '../assessmentPage/selectors';

export const assessmentSummary = (state: RootReducer) =>
  state.assessmentSummary.summary;

const documentReviews = (state: RootReducer) =>
  state.assessmentSummary.summary?.documentReviews;

export const getDocumentMetadata = (state: RootReducer) =>
  state.assessmentSummary.summary?.documentReviews?.metadata;

export const getDocumentMetadataState = (state: RootReducer) =>
  state.assessmentSummary.documentMetadataChecksSelected;

export const getDocumentMetadataType = (state: RootReducer) =>
  state.assessmentSummary.documentMetadataChecksSelected_type;

export const documentAlerts = (state: RootReducer) =>
  state.assessmentSummary.summary?.documentAlerts;

export const shouldRenderSnackBar = createSelector(
  selectedCategory,
  documentReviews,
  isBrandedDocument,
  (selectedCategory, documentReviews, isBrandedDocument): boolean => {
    if (!documentReviews || selectedCategory !== Review.branding) {
      return false;
    }

    const reviewInfo = documentReviews[DocumentCategory.brandingReview];
    if (isBrandedDocument) {
      return reviewInfo.result === ReviewResult.fail;
    } else {
      return reviewInfo.checks.some(
        check => check.result === ReviewResult.pass
      );
    }
  }
);

export const getBrandingReview = createSelector(
  shouldRenderSnackBar,
  documentReviews,
  isBrandedDocument,
  (
    shouldRenderSnackBar,
    documentReviews,
    isBrandedDocument
  ): ChecksBrandingAlert | undefined => {
    if (!shouldRenderSnackBar || !documentReviews) {
      return undefined;
    }

    let reviewResult = ReviewResult.pass;
    const checks: Check[] = documentReviews[
      DocumentCategory.brandingReview
    ].checks
      .filter((check: CheckPayload) => {
        if (isBrandedDocument) {
          return check.result === ReviewResult.fail;
        }
        return check.result === ReviewResult.pass;
      })
      .map((check: CheckPayload) => {
        if (check.result === ReviewResult.fail) {
          reviewResult = ReviewResult.fail;
        }
        return { ...check } as Check;
      });

    return {
      id: nanoid(),
      result: reviewResult,
      checks,
    };
  }
);

export const isBrandingSelected = createSelector(
  getBrandingReview,
  selectedCheck,
  (getBrandingReview, selectedCheck): boolean =>
    getBrandingReview?.id === selectedCheck
);

export const getTotalSsiConsistenessFail = (state: RootReducer): number => {
  const {
    assessmentSummary: { summary },
  } = state;

  if (!summary?.documentAlerts) {
    return 0;
  }

  return summary.documentAlerts.ssiCompleteness.fail;
};

export const getTotalStatisticByStatus = createSelector(
  documentAlerts,
  (documentAlerts): StatisticByStatus => {
    const defaultState = {
      [ReviewResult.fail]: 0,
      [ReviewResult.pass]: 0,
      [ReviewResult.warning]: 0,
    } as StatisticByStatus;

    if (!documentAlerts) {
      return defaultState;
    }

    const result = COUNTABLE_STATS_CATEGORIES.reduce((result, category) => {
      const documentCategory = documentAlerts[category];

      if (documentCategory) {
        result[ReviewResult.fail] += documentCategory[ReviewResult.fail];
        result[ReviewResult.pass] += documentCategory[ReviewResult.pass];
        result[ReviewResult.warning] += documentCategory[ReviewResult.warning];
      }

      return result;
    }, defaultState);

    return result;
  }
);

export const getTotalSafetyStatistic = createSelector(
  documentAlerts,
  (documentAlerts): StatisticByStatus => {
    const defaultState = {
      [ReviewResult.fail]: 0,
      [ReviewResult.pass]: 0,
      [ReviewResult.warning]: 0,
    } as StatisticByStatus;

    if (!documentAlerts) {
      return defaultState;
    }

    return documentAlerts[Review.safety];
  }
);

export const getTotalStyleStatistic = createSelector(
  documentAlerts,
  (documentAlerts): StatisticByStatus => {
    const defaultState = {
      [ReviewResult.fail]: 0,
      [ReviewResult.pass]: 0,
      [ReviewResult.warning]: 0,
    } as StatisticByStatus;

    if (!documentAlerts) {
      return defaultState;
    }

    return documentAlerts[Review.style];
  }
);

export const getTotalCount = createSelector(
  getTotalStatisticByStatus,
  getTotalSsiConsistenessFail,
  (statisticByStatus, totalSsiCompleteness): number =>
    Object.values(statisticByStatus).reduce((result, category) => {
      return result + category;
    }, totalSsiCompleteness as number)
);

export const getPercentage = (count: number, all: number): string => {
  if (!all) {
    return '0%';
  }

  return ((count / all) * 100).toFixed() + '%';
};

export const getDocumentPassPercentage = createSelector(
  getTotalStatisticByStatus,
  getTotalCount,
  (statisticByStatus, totalCount): string =>
    getPercentage(statisticByStatus[ReviewResult.pass], totalCount)
);

export const getDocumentPassSafetyPercentage = createSelector(
  getTotalSafetyStatistic,
  getTotalSsiConsistenessFail,
  (totalCount, totalSsiCompleteness): string => {
    const totalSafetyAlerts = Object.keys(totalCount).reduce(
      (acc, rec): number => {
        return acc + totalCount[rec as keyof typeof totalCount];
      },
      totalSsiCompleteness
    );

    return getPercentage(totalCount.pass, totalSafetyAlerts);
  }
);

export const getDocumentPassStylePercentage = createSelector(
  getTotalStyleStatistic,
  (totalCount): string => {
    const totalStyleAlerts = Object.keys(totalCount).reduce(
      (acc, rec): number => {
        return acc + totalCount[rec as keyof typeof totalCount];
      },
      0
    );

    return getPercentage(totalCount.pass, totalStyleAlerts);
  }
);

export const getAlertCountByStatus = createSelector(
  documentAlerts,
  (
    _: any,
    { category, result }: { category: Review; result: ReviewResult }
  ) => ({
    category,
    result,
  }),
  (documentAlerts, { category, result }): number => {
    if (!documentAlerts) {
      return 0;
    }
    if (result === ReviewResult.skip) {
      return 0;
    }
    if (category === Review.branding) {
      return 0;
    }

    return documentAlerts[category][result];
  }
);

export const getAlertCountByCategory = (
  state: RootReducer,
  category: Review
): number =>
  ReviewResultList.reduce(
    (acc, result) =>
      acc +
      getAlertCountByStatus(state, {
        category,
        result,
      }),
    0
  );
